import m from "mithril";
import * as tus from "tus-js-client";
import Requester from "../../../shared/request";
import Gateway from "../../../shared/app_settings";
import i18n from "../../../shared/i18n/i18n";
import Toasts from "../../../shared/toasts";
import ModalManager from "../../../shared/modal";
import Modal_Confirm_Delete from "../../../shared/components/modal_confirm_delete";
import Auth from "../../../shared/auth";
import Modal_Unit_File_History from "../modal_unit_file_history";
import RouterService from "../../../shared/services/router.service";
import FileUploader from '../../../shared/components/component_file_uploader';
import {getUnitActiveStatusSlider} from "../unit_status_slider_util";
import {loadCourses} from "../unit_courses";

let loading, saving, uploading, processing, deleting, unit;
let uploadProgress = 0;
let onCoursesClick;
let processingProgress = 0;
let processingMessage = "";
let pollTimer = null;
let pollRetryCount = 0;
let currentUpload = null;
const POLL_MAX_RETRIES = 5;
// tus resumable upload chunk size (25MB). Tuned for the ~1GB SCORM use case: ~41 chunks for a 1GB file,
// Must stay under the ingress/proxy single-request body limit.
const TUS_CHUNK_SIZE = 25 * 1024 * 1024;
const SCORM_JOB_ID_HEADER = "x-scorm-job-id";

function saveUnitDetails() {
    saving = true;
    Requester.post(Gateway.admin + "units/interactive/" + unit.id, {
        id: unit.id,
        name: unit.name,
        description: unit.description
    })
        .then(function (result) {
            Toasts.add("success", i18n.t("saved_changes"));
        })
        .catch(function (error) {
            Toasts.add("error", i18n.t("error"), i18n.t(error.response.properties.errorCode));
        })
        .then(function () {
            saving = false;
        });
}

function pollUploadStatus(unitId, jobId) {
    Requester.get(Gateway.admin + "units/interactive/" + unitId + "/upload-status/" + jobId)
        .then(function (result) {
            pollRetryCount = 0;
            processingProgress = result.progress || 0;
            processingMessage = result.message || "";

            if (result.status === "complete") {
                processing = false;
                Toasts.add("success", i18n.t("saved_changes"), "");
                m.redraw();
            } else if (result.status === "failed") {
                processing = false;
                Toasts.add("error", i18n.t("error"), result.errorMessage || i18n.t("SharedInternalServer"));
                m.redraw();
            } else {
                m.redraw();
                pollTimer = setTimeout(function () { pollUploadStatus(unitId, jobId); }, 2000);
            }
        })
        .catch(function (error) {
            const status = error && error.response && error.response.status;

            // Stop immediately on 404 or any other 4xx — the job is gone or the request is bad
            if (status >= 400 && status < 500) {
                processing = false;
                const msg = (error.response.properties && error.response.properties.errorCode)
                    ? i18n.t(error.response.properties.errorCode)
                    : i18n.t("SharedInternalServer");
                Toasts.add("error", i18n.t("error"), msg);
                m.redraw();
                return;
            }

            // Transient network/5xx error — retry up to the cap
            pollRetryCount++;
            if (pollRetryCount >= POLL_MAX_RETRIES) {
                processing = false;
                Toasts.add("error", i18n.t("error"), i18n.t("SharedInternalServer"));
                m.redraw();
                return;
            }

            m.redraw();
            pollTimer = setTimeout(function () { pollUploadStatus(unitId, jobId); }, 3000);
        });
}

function uploadScorm() {
    const fileInput = document.getElementById("scorm-file-input");
    const file = fileInput && fileInput.files[0];
    if (!file) {
        Toasts.add("error", i18n.t("cant_save"), i18n.t("select_file_to_upload"));
        return;
    }

    saving = false;
    uploading = true;
    processing = false;
    uploadProgress = 0;
    processingProgress = 0;
    processingMessage = "";
    pollRetryCount = 0;

    currentUpload = new tus.Upload(file, {
        endpoint: Gateway.admin + "units/interactive/" + unit.id + "/upload-file-stream",
        chunkSize: TUS_CHUNK_SIZE,
        headers: Requester.headers(),
        retryDelays: [0, 1000, 3000, 5000, 10000],
        metadata: {
            filename: file.name,
            filetype: file.type || "application/zip"
        },
        onBeforeRequest: function(req) {
            // Session cookie auth, equivalent to Requester's withCredentials behaviour.
            var xhr = req.getUnderlyingObject();
            xhr.withCredentials = true;
        },
        onProgress: function (bytesUploaded, bytesTotal) {
            uploadProgress = bytesTotal > 0 ? Math.round((bytesUploaded / bytesTotal) * 100) : 0;
            m.redraw();
        },
        onSuccess: function (payload) {
            // Transfer done. Server has created the job and started extraction in the background;
            // switch to polling upload-status
            // tus-js-client 4.x passes { lastResponse }; the job id comes back in a response header.
            const lastResponse = payload && payload.lastResponse;
            const jobId = lastResponse ? lastResponse.getHeader(SCORM_JOB_ID_HEADER) : null;

            uploading = false;
            currentUpload = null;

            if (!jobId) {
                Toasts.add("error", i18n.t("error"), i18n.t("SharedInternalServer"));
                m.redraw();
                return;
            }

            processing = true;
            processingProgress = 0;
            processingMessage = i18n.t("extracting") || "Extracting...";
            m.redraw();
            pollUploadStatus(unit.id, jobId);
        },
        onError: function (error) {
            uploading = false;
            currentUpload = null;
            const status = error && error.originalResponse && error.originalResponse.getStatus
                && error.originalResponse.getStatus();
            Toasts.add("error", i18n.t("error"),
                status === 413 ? i18n.t("file_size_exceeds_limit") : i18n.t("SharedInternalServer"));
            m.redraw();
        }
    });

    currentUpload.start();
}

function confirmDeleteUnit() {
    ModalManager.open(i18n.t("delete_unit"), [], Modal_Confirm_Delete, {callback: deleteUnit});
}

function deleteUnit() {
    deleting = true;
    Requester.delete(Gateway.admin + "units/interactive/" + unit.id)
        .then(function () {
            RouterService.navigateToUnits();
        })
        .catch(function (error) {
            Toasts.add("error", i18n.t("error"), i18n.t(error.response.properties.errorCode));
        })
        .finally(function () {
            deleting = false;
        });
}

function showHistory() {
    loading = true;
    Requester.get(Gateway.admin + "units/interactive/" + unit.id + "/history")
        .then(function (result) {
            if (result && result.length > 0)
                ModalManager.open(i18n.t("history"), ["modal-wide"], Modal_Unit_File_History, {history: result, metadataTitle: i18n.t("scorm_version")});
            else
                Toasts.add("warn", i18n.t("no_history_found"));
        })
        .catch(function (error) {
            Toasts.add("error", i18n.t("error"), i18n.t(error.response.properties.errorCode));
        })
        .finally(function () {
            loading = false;
        });
}

const Component_Unit_Scorm_Settings = {
    oninit: function (vnode) {
        unit = vnode.attrs.unit;
        onCoursesClick = vnode.attrs.onCoursesClick;
        loadCourses(unit.id);
    },
    onremove: function () {
        clearTimeout(pollTimer);
        if (currentUpload) {
            currentUpload.abort();
            currentUpload = null;
        }
    },
    view: function () {
        const busy = uploading || processing;
        return [
            m(".content-box", [
                m("h3", i18n.t("details")),
                m(".form", {class: !Auth.validateRights(["unit_scorm_edit"]) ? "disabled" : ""}, [
                    m(".form-section required", [
                        m(".form-label", i18n.t("name")),
                        m("input[type=text]", {
                            oninput: function () {
                                unit.name = this.value;
                            }, value: unit.name
                        })
                    ]),
                    m(".form-section", [
                        m(".form-label", i18n.t("description")),
                        m("textarea", {
                            oninput: function () {
                                unit.description = this.value;
                            }, value: unit.description
                        })
                    ]),
                    m(".form-buttons", [
                        !saving && !busy ? m("button", {onclick: saveUnitDetails}, i18n.t("save")) : m("button.btn-text no-click", [i18n.t("saving"), m(".loading-dots")])
                    ])
                ])
            ]),
            m(".content-box", [
                m("h3", i18n.t("content")),
                m(".form", [
                    m(".form-section", {class: !Auth.validateRights(["unit_scorm_view"]) ? "disabled" : ""}, [
                        m(".flex-horizontal-content mb-1", unit.scormFileName ? [
                            i18n.t("current_file") + ": " + unit.scormFileName, " ", m("button.btn-text", {
                                onclick: showHistory,
                                class: loading ? "disabled loading-dots" : ""
                            }, i18n.t("history"))
                        ] : ""),
                        m("", {class: !Auth.validateRights(["unit_scorm_edit"]) ? "disabled" : ""}, [
                            m(".form-label inline-tip", {"data-tip": "1GB max"}, i18n.t("scorm_package")),
                            m(FileUploader, {
                                id: "scorm-file-input",
                                accept: ".zip",
                                maxSizeInMB: 1024, //1GB
                            }),
                            uploading ? [
                                m(".form-label mt-1", (i18n.t("uploading") || "Uploading..."),
                                    uploadProgress > 0 ? " " + uploadProgress + "%" : ""
                                ),
                                m(".loading-bar mt-1", [
                                    m(".loading-width", {
                                        style: {width: uploadProgress + "%"},
                                        class: uploadProgress >= 100 ? "upload-complete" : ""
                                    })
                                ])
                            ] : "",
                            processing ? [
                                m(".form-label mt-1", processingMessage || (i18n.t("processing") || "Processing..."),
                                    processingProgress > 0 ? " " + processingProgress + "%" : ""
                                ),
                                m(".loading-bar mt-1", [
                                    m(".loading-width", {
                                        style: {width: processingProgress + "%"},
                                        class: processingProgress >= 100 ? "upload-complete" : ""
                                    })
                                ])
                            ] : ""
                        ])
                    ]),
                    m(".form-buttons", {class: !Auth.validateRights(["unit_scorm_edit"]) ? "disabled" : ""}, [
                        !busy ? m("button", {onclick: uploadScorm}, i18n.t("upload")) : m("button.btn-text no-click", [
                            uploading ? i18n.t("uploading") : (processingMessage || (i18n.t("processing") || "Processing")),
                            m(".loading-dots")
                        ])
                    ])
                ])
            ]),
            m(".content-box", [
                m(".form", {class: !Auth.validateRights(["unit_scorm_edit"]) ? "disabled" : ""}, [
                    getUnitActiveStatusSlider(unit, saving, onCoursesClick)
                ])
            ]),
            Auth.validateRights(["unit_scorm_delete"], [
                m(".content-box", [
                    !deleting ? m("button.btn-error", {onclick: confirmDeleteUnit}, i18n.t("delete_unit")) : m("button.btn-text no-click text-error", [i18n.t("deleting"), m(".loading-dots")])
                ])
            ])
        ];
    }
};
export default Component_Unit_Scorm_Settings
